/**
 * DeviceRemovalProgress
 * Progress modal showing bulk removal status
 */

import React from 'react';
import { X, CheckCircle, Clock, XCircle } from 'lucide-react';

interface DeviceStatus {
  deviceName: string;
  status: 'pending' | 'success' | 'timeout' | 'error';
}

interface DeviceRemovalProgressProps {
  isOpen: boolean;
  currentIndex: number;
  totalCount: number;
  currentVisitorName: string;
  deviceStatuses?: DeviceStatus[];
  onClose?: () => void; // Optional close (only show after completion)
}

export const DeviceRemovalProgress: React.FC<DeviceRemovalProgressProps> = ({
  isOpen,
  currentIndex,
  totalCount,
  currentVisitorName,
  deviceStatuses = [],
  onClose,
}) => {
  if (!isOpen) return null;

  const progressPercent = totalCount > 0 ? Math.round((currentIndex / totalCount) * 100) : 0;
  const isComplete = currentIndex >= totalCount;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg border border-gray-800 shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-lg font-semibold text-white">
            {isComplete ? 'Revogação Concluída' : 'Revogando Visitantes...'}
          </h2>
          {isComplete && onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div className="p-4 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-300">
                {currentIndex} / {totalCount} visitante{totalCount !== 1 ? 's' : ''}
              </span>
              <span className="text-gray-400">{progressPercent}%</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-red-500 h-full transition-all duration-300 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Current Visitor */}
          {!isComplete && currentVisitorName && (
            <div className="text-sm text-gray-300 text-center py-2">
              <span className="text-gray-500">Revogando:</span>{' '}
              <span className="font-medium">{currentVisitorName}</span>
            </div>
          )}

          {/* Device Statuses (if provided) */}
          {deviceStatuses.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Status dos Dispositivos</p>
              {deviceStatuses.map((device, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm bg-gray-800/50 rounded px-3 py-2"
                >
                  <span className="text-gray-300 truncate">{device.deviceName}</span>
                  <div className="flex items-center gap-1.5">
                    {device.status === 'success' && (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-green-500 text-xs">OK</span>
                      </>
                    )}
                    {device.status === 'timeout' && (
                      <>
                        <Clock className="w-4 h-4 text-yellow-500" />
                        <span className="text-yellow-500 text-xs">Tempo esgotado</span>
                      </>
                    )}
                    {device.status === 'error' && (
                      <>
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span className="text-red-500 text-xs">Falhou</span>
                      </>
                    )}
                    {device.status === 'pending' && (
                      <span className="text-gray-500 text-xs">Aguardando...</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Completion Message */}
          {isComplete && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-gray-300">
                {totalCount} visitante{totalCount !== 1 ? 's' : ''} revogado{totalCount !== 1 ? 's' : ''} com sucesso
              </p>
            </div>
          )}

          {/* Cannot Cancel Warning */}
          {!isComplete && (
            <p className="text-xs text-gray-500 text-center">
              Não é possível cancelar. Por favor, aguarde...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
